import type { Json, VersionedJsonDocument } from "../database.types";
import { validateVersionedJson, type ValidationResult } from "../validation";

export type JsonRecord = { [key: string]: Json | undefined };

export interface VersionedPayloadCodec<T extends Json> {
  readonly label: string;
  readonly supportedVersions: readonly number[];
  encode(data: T, schemaVersion?: number): VersionedJsonDocument;
  decode(value: unknown, storedSchemaVersion: number): ValidationResult<VersionedJsonDocument>;
}

const isJsonRecord = (value: Json): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function createVersionedRecordCodec(
  label: string,
  supportedVersions: readonly number[] = [1],
): VersionedPayloadCodec<JsonRecord> {
  return {
    label,
    supportedVersions,
    encode(data, schemaVersion = supportedVersions[0]) {
      if (!supportedVersions.includes(schemaVersion)) {
        throw new Error(`${label} schema version ${schemaVersion} is not supported.`);
      }
      if (!isJsonRecord(data)) throw new Error(`${label} data must be a JSON object.`);
      return { schemaVersion, data };
    },
    decode(value, storedSchemaVersion) {
      const result = validateVersionedJson(value, supportedVersions, label);
      if (!result.ok) return result;
      if (result.value.schemaVersion !== storedSchemaVersion) {
        return {
          ok: false,
          error: {
            code: "invalid_payload",
            message: `${label} schema version does not match the stored schema version.`,
          },
        };
      }
      if (!isJsonRecord(result.value.data)) {
        return {
          ok: false,
          error: { code: "invalid_payload", message: `${label} data must be a JSON object.` },
        };
      }
      return result;
    },
  };
}

export const organisationDetailsCodec = createVersionedRecordCodec("Organisation details");
export const auditDetailsCodec = createVersionedRecordCodec("Audit details");
export const reportMetadataCodec = createVersionedRecordCodec("Report metadata");
export const reportVersionMetadataCodec = createVersionedRecordCodec("Report version metadata");
export const sectionWorkflowCodec = createVersionedRecordCodec("Section workflow payload");
export const evidencePayloadCodec = createVersionedRecordCodec("Evidence payload");
export const clientDraftCodec = createVersionedRecordCodec("Client draft payload");
